import { useMemo, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ListBulletIcon,
  CheckIcon,
  XMarkIcon,
  CurrencyEuroIcon,
} from '@heroicons/react/24/outline';

import Header from 'common/components/Header/Header';
import useList from 'common/hooks/useList';
import FilteredList from 'common/components/Table/FilteredList';
import RadioBlock from 'common/components/RadioBlock/RadioBlock';
import TabBlockItem from 'common/components/TabBlockItem/TabBlockItem';

import AlertsContext from 'common/providers/alerts';
import UserSearch from 'components/search/UserSearch';
import { Payment } from 'types/Payment';
import PaymentItem from './PaymentItem';

function Payments() {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const {
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    itemsCount,
    itemsPerPage,
    error: errorList1,
  } = useList<Payment>('payments', {
    defaultSorts: ['executionDate:DESC', 'createdAt:DESC'],
    populate: ['profile', 'profile.avatar', 'profile.companyLogo', 'user', 'invoice'],
  });

  const { items: payments, fetchItems: fetchPayments } = useList<Payment>('payments', {
    fields: ['id', 'status', 'executionDate'],
    manual: true,
  });

  useEffect(() => {
    if (errorList1) {
      setAlert(errorList1, 'danger');
    }
  }, [errorList1, setAlert]);

  const statusList = useMemo(() => {
    return [
      {
        id: '',
        name: t('status.all'),
        count: payments?.length || 0,
        icon: ListBulletIcon,
        color: 'primary',
      },
      {
        id: 'success',
        name: t('status.success'),
        count: payments?.filter((p: Payment) => p.status === 'success')?.length || 0,
        icon: CheckIcon,
        color: 'green',
      },
      {
        id: 'error',
        name: t('status.error'),
        count: payments?.filter((p: Payment) => p.status === 'error')?.length || 0,
        icon: XMarkIcon,
        color: 'red',
      },
    ];
  }, [t, payments]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const columns = useMemo(
    () => [
      {
        Header: t('forms.associatedProfile'),
        filterName: 'profile.username$containsi',
        filterLabel: t('filters.search'),
        columnClasses: 'md:flex-1',
        Filter: UserSearch,
        filterComponent: (props: any) => <UserSearch isFilter {...props} />,
      },
    ],
    [t]
  );

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <Header
        title={t('titlePages.payments')}
        Icon={CurrencyEuroIcon}
        subTitle={t('titleLegends.payments')}
        isBeforeFilters
        filters={filters}
        setFilter={setFilter}
        filterName="status"
        FilterComponent={(props: any) => (
          <RadioBlock Component={TabBlockItem} items={statusList} horizontal noGap {...props} />
        )}
      />
      <FilteredList
        data={items ?? []}
        columns={columns}
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={PaymentItem}
        onDelete={null}
        onNavigate={null}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Payments;
